'use client';

import { DeleteOutlined as DeleteIcon } from '@mui/icons-material';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import {
	DataGridPro,
	GridActionsCellItem,
	GridColDef,
	GridRenderEditCellParams,
	GridRowId,
	useGridApiRef,
} from '@mui/x-data-grid-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider, PickersLayout, pickersLayoutClasses } from '@mui/x-date-pickers-pro';
import { gql, useMutation } from '@parkchirp/api-client';
import { adaptGridValueCallback } from '@parkchirp/ui-library';
import type { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { dayjs, normalizeState, provinceFieldOptions, stateFieldOptions } from '../../../utils';

export const SAVE_VEHICLE_HISTORY = gql`
  mutation saveVehicleHistory(
    $vehicleId: String!
    $history: [VehicleHistoryInput]
    $facilityId: String!
    $tenantSubscriptionId: String
  ) {
    saveVehicleHistory(
      vehicleId: $vehicleId
      history: $history
      facilityId: $facilityId
      tenantSubscriptionId: $tenantSubscriptionId
    )
  }
`;

const isCurrentHistoryRow = (row) => {
	const today = dayjs.utc().startOf('day');
	const startOk = dayjs.utc(row.activeDate).isSameOrBefore(today, 'day');
	const endOk = !row.inactiveDate || dayjs.utc(row.inactiveDate).isAfter(today, 'day');
	return startOk && endOk;
};

const setCellValue = (
	api: GridRenderEditCellParams['api'],
	id: GridRowId,
	field: string,
	value: any,
	setVehicleHistoryChanged: (changed: boolean) => void,
) => {
	setVehicleHistoryChanged(true);
	api.setEditCellValue({ id, field, value }, { debounceMs: 0 });
};

const InactiveDateDisplayCell = (params) => {
	const { id, field, value, row, api, colDef, isEditMode } = params;
	if (!isEditMode) {
		const value = params.value ? dayjs.utc(params.value).format('MM/DD/YYYY') : '';
		return <span>{value}</span>;
	}

	const activeMin = row?.activeDate ? dayjs.utc(row.activeDate).startOf('day') : null;

	const todayMin = dayjs.utc().startOf('day');

	const minDate = activeMin && activeMin.isAfter(todayMin) ? activeMin : todayMin;

	const maxDate = row?.rowEndDate ? dayjs.utc(row.rowEndDate).startOf('day') : undefined;

	const handleClick = () => {
		if (!colDef.editable) return;
		if (row.inactiveDate) return; // only allow editing if inactiveDate is null

		api.startCellEditMode({ id, field });
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<DatePicker<Dayjs>
				timezone='UTC'
				value={value ? dayjs.utc(value) : null}
				minDate={minDate?.isValid() ? minDate : undefined}
				maxDate={maxDate?.isValid() ? maxDate : undefined}
				format='MM/DD/YYYY'
				readOnly
				slotProps={{
					textField: {
						size: 'small',
						fullWidth: true,
						InputProps: {
							readOnly: true,
						},
						onClick: handleClick,
						sx: {
							cursor: colDef.editable ? 'pointer' : 'default',
						},
					},
				}}
			/>
		</LocalizationProvider>
	);
};

const InactiveDateEditCell = (params: GridRenderEditCellParams) => {
	const { id, field, value, api, row, setVehicleHistoryChanged } = params;

	const [open, setOpen] = React.useState(true);

	const activeMin = row?.activeDate ? dayjs.utc(row.activeDate).startOf('day') : null;

	const todayMin = dayjs.utc().startOf('day');

	const minDate = activeMin && activeMin.isAfter(todayMin) ? activeMin : todayMin;

	const maxDate = row?.rowEndDate ? dayjs.utc(row.rowEndDate).startOf('day') : undefined;

	const stopEditing = () => {
		api.stopCellEditMode({ id, field });
	};

	const setValue = (val) => {
		const column = api.getColumn(field);
		if (!column.editable) return;
		const row = api.getRow(id);
		if (!row) return;
		if (val) {
			if (minDate && val.isBefore(minDate, 'day')) return;
			if (maxDate && val.isAfter(maxDate, 'day')) return;
		}

		setVehicleHistoryChanged(true);

		const nextValue = val ? val.startOf('day').valueOf() : null;

		api.setEditCellValue({ id, field, value: nextValue }, { debounceMs: 0 });

		setOpen(false);
		stopEditing();
	};
	const QuickActions = ({ onPick, onClear }: { onPick: (d) => void; onClear: () => void }) => {
		const canPick = (d) => {
			if (minDate && d.isBefore(minDate, 'day')) return false;
			if (maxDate && d.isAfter(maxDate, 'day')) return false;
			return true;
		};

		const yesterday = dayjs.utc().subtract(1, 'day');
		const today = dayjs.utc();
		const tomorrow = dayjs.utc().add(1, 'day');
		const nextWeek = dayjs.utc().add(1, 'week');

		return (
			<Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '5px' }}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
					<Button size='small' disabled={!canPick(yesterday)} onClick={() => onPick(yesterday)}>
						Yesterday
					</Button>
					<Button size='small' disabled={!canPick(today)} onClick={() => onPick(today)}>
						Today
					</Button>
					<Button size='small' disabled={!canPick(tomorrow)} onClick={() => onPick(tomorrow)}>
						Tomorrow
					</Button>
					<Button size='small' disabled={!canPick(nextWeek)} onClick={() => onPick(nextWeek)}>
						Next Week
					</Button>
				</Box>
				<Button sx={{ backgroundColor: '#607d8b' }} size='small' onClick={onClear}>
					Clear
				</Button>
			</Box>
		);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<DatePicker<Dayjs>
				open={open}
				onOpen={() => setOpen(true)}
				onClose={() => {
					setOpen(false);
					stopEditing();
				}}
				autoFocus
				timezone='UTC'
				value={value ? dayjs.utc(value) : null}
				minDate={minDate?.isValid() ? minDate : undefined}
				maxDate={maxDate?.isValid() ? maxDate : undefined}
				onChange={setValue}
				format='MM/DD/YYYY'
				slots={{
					layout: (props) => (
						<Box sx={{ display: 'flex', gap: 1 }}>
							<QuickActions onPick={(d) => setValue(d)} onClear={() => setValue(null)} />
							<PickersLayout {...props} />
						</Box>
					),
				}}
				slotProps={{
					textField: {
						size: 'small',
						fullWidth: true,
					},
					layout: {
						sx: {
							[`& .${pickersLayoutClasses.actionBar}`]: {
								display: 'none',
							},
						},
					},
				}}
			/>
		</LocalizationProvider>
	);
};

const SelectEditCell = (params: GridRenderEditCellParams, options: { value: string; label: string }[]) => {
	const { id, field, value, api, setVehicleHistoryChanged } = params;
	return (
		<TextField
			select
			value={value || ''}
			onChange={(e) => setCellValue(api, id, field, e.target.value, setVehicleHistoryChanged)}
			style={{ width: '100%' }}
			size='small'
		>
			{options.map((option) => (
				<MenuItem key={option.value} value={option.value}>
					{option.value}
				</MenuItem>
			))}
		</TextField>
	);
};

const TextEditCell = (params: GridRenderEditCellParams) => {
	const { id, field, value, api, setVehicleHistoryChanged } = params;
	return (
		<TextField
			value={value || ''}
			onChange={(e) => setCellValue(api, id, field, e.target.value, setVehicleHistoryChanged)}
			size='small'
			fullWidth
		/>
	);
};

const getGridEditActions = (handleDeleteClick: () => void, showDelete: boolean) => {
	if (!showDelete) return [];
	return [
		<GridActionsCellItem
			key='delete'
			icon={<DeleteIcon />}
			label='Delete'
			onClick={() => handleDeleteClick()}
			color='inherit'
			data-cy='delete-row-button'
		/>,
	];
};

const getHistoryColumns = ({ handleDeleteClick, rows, isEditMode, setVehicleHistoryChanged }): GridColDef[] => {
	return [
		{
			field: 'actions',
			type: 'actions' as const,
			headerName: 'Actions',
			flex: 1.5,
			getActions: (params) => {
				const lastRowId = rows[rows.length - 1]?.id;
				const isLastRow = params.id === lastRowId && rows.length > 1;

				const isNew = !!params.row.isNew;
				const isFuture =
					params.row.activeDate && dayjs.utc(params.row.activeDate).isAfter(dayjs.utc().startOf('day'));

				const showDelete = isLastRow && (isNew || isFuture);
				return getGridEditActions(handleDeleteClick, showDelete);
			},
		},
		{
			field: 'activeDate',
			headerName: 'Active Date',
			flex: 1.5,
			valueGetter: adaptGridValueCallback(({ row }) => dayjs.utc(row.activeDate).format('MM/DD/YYYY')),
			resizable: false,
			sortable: false,
		},
		{
			field: 'inactiveDate',
			headerName: 'Inactive Date',
			flex: 1.5,
			editable: true,
			valueFormatter: adaptGridValueCallback(({ value }) => (value ? dayjs.utc(value).format('MM/DD/YYYY') : '')),
			renderCell: (params) => <InactiveDateDisplayCell isEditMode={isEditMode} {...params} />,
			renderEditCell: isEditMode
				? (params) => <InactiveDateEditCell {...params} setVehicleHistoryChanged={setVehicleHistoryChanged} />
				: undefined,
			resizable: false,
			sortable: false,
		},
		{
			field: 'licensePlate',
			headerName: 'License',
			flex: 1,
			editable: true,
			resizable: false,
			sortable: false,
			renderEditCell: (params) => (
				<TextEditCell {...params} setVehicleHistoryChanged={setVehicleHistoryChanged} />
			),
		},
		{
			field: 'licenseState',
			headerName: 'State/Province',
			valueFormatter: adaptGridValueCallback(({ value }) => {
				return normalizeState(value) || '';
			}),
			flex: 1,
			editable: true,
			resizable: false,
			sortable: false,
			renderEditCell: (params) =>
				SelectEditCell({ ...params, setVehicleHistoryChanged }, [
					...stateFieldOptions,
					...provinceFieldOptions,
				]),
		},
		{
			field: 'color',
			headerName: 'Color',
			flex: 1,
			editable: true,
			resizable: false,
			sortable: false,
			renderEditCell: (params) => (
				<TextEditCell {...params} setVehicleHistoryChanged={setVehicleHistoryChanged} />
			),
		},
		{
			field: 'decal',
			headerName: 'Decal',
			flex: 1,
			editable: true,
			resizable: false,
			sortable: false,
			renderEditCell: (params) => (
				<TextEditCell {...params} setVehicleHistoryChanged={setVehicleHistoryChanged} />
			),
		},
		{
			field: 'createdBy',
			headerName: 'Updated By',
			flex: 2,
			resizable: false,
			sortable: false,
		},
	];
};

export const VehicleHistoryPanel = ({
	history = [],
	vehicleId,
	facilityId,
	setShowSnackbar,
	tenantSubscriptionId,
	rowEndDate,
	isEditMode,
	setVehicleHistoryChanged,
}) => {
	const [saveVehicleHistory, { loading: isSaving }] = useMutation(SAVE_VEHICLE_HISTORY, {
		refetchQueries: ['getMonthlyParkerVehicles'],
	});
	const apiRef = useGridApiRef();

	const mappedHistory = React.useMemo(
		() =>
			history.map((h) => ({
				...h,
				createdBy: h.createdByUser?.email,
				rowEndDate,
			})),
		[history, rowEndDate],
	);

	const [rows, setRows] = useState(mappedHistory);

	useEffect(() => {
		setRows(mappedHistory);
	}, [mappedHistory]);

	const handleDeleteClick = () => {
		setRows((prev) => {
			const next = prev.slice(0, -1);
			const last = next.at(-1);
			next[next.length - 1] = { ...last, inactiveDate: null };

			return next;
		});
	};

	const processRowUpdate = (newRow) => {
		setRows((prev) => {
			const idx = prev.findIndex((r) => r.id === newRow.id);
			if (idx === -1) return prev;

			const next = [...prev];
			next[idx] = newRow;

			const inactive = newRow.inactiveDate ? dayjs.utc(newRow.inactiveDate) : null;
			if (!inactive || !inactive.isValid()) return next;

			const nextActiveDate = inactive.startOf('day').add(1, 'day').valueOf();

			const nextRowIndex = next.findIndex((r, i) => i > idx && r.previousRowId === newRow.id);

			if (nextRowIndex !== -1) {
				next[nextRowIndex] = { ...next[nextRowIndex], activeDate: nextActiveDate };
			} else {
				next.splice(idx + 1, 0, {
					id: uuidv4(),
					previousRowId: newRow.id,
					activeDate: nextActiveDate,
					inactiveDate: null,
					licenseState: newRow.licenseState,
					licensePlate: newRow.licensePlate,
					color: newRow.color,
					decal: newRow.decal,
					createdBy: newRow.createdBy,
					isNew: true,
				});
			}
			return next;
		});

		return newRow;
	};

	const columns = getHistoryColumns({
		handleDeleteClick,
		rows,
		isEditMode,
		setVehicleHistoryChanged,
	});

	const handleSave = async () => {
		const latestRows = Array.from(apiRef.current.getRowModels().values());

		const formattedHistory = latestRows.map(
			({ createdByUser, createdBy, isNew, previousRowId, rowEndDate, ...rest }) => {
				const { id, activeDate, inactiveDate } = rest;
				return {
					...rest,
					id: typeof id === 'number' ? id : null,
					activeDate: dayjs.utc(activeDate).toISOString(),
					inactiveDate: inactiveDate ? dayjs.utc(inactiveDate).toISOString() : null,
				};
			},
		);

		let message = 'Successfully saved vehicle history';
		let severity = 'success';
		try {
			await saveVehicleHistory({
				variables: {
					vehicleId,
					history: formattedHistory,
					facilityId,
					tenantSubscriptionId,
				},
				context: { apiVersion: 'v2' },
			});
			setVehicleHistoryChanged(false);
		} catch (err) {
			message = err.message;
			severity = 'error';
		} finally {
			setShowSnackbar({ message, severity });
		}
	};

	return (
		<Box sx={{ p: 2, background: '#fafafa', borderRadius: 2 }}>
			<div style={{ display: 'flex', width: '100%', alignItems: 'center', marginBottom: '10px' }}>
				<Typography fontWeight='bold' sx={{ marginRight: '5px' }}>
					Change History
				</Typography>
				{isEditMode && (
					<Button loading={isSaving} onClick={handleSave}>
						Save
					</Button>
				)}
			</div>
			<DataGridPro
				apiRef={apiRef}
				rows={rows}
				columns={columns}
				autoHeight
				hideFooter
				disableRowSelectionOnClick
				initialState={{ density: 'compact' }}
				editMode='cell'
				processRowUpdate={processRowUpdate}
				isCellEditable={(params) => {
					if (!isEditMode) return false;
					if (isCurrentHistoryRow(params.row) && params.field !== 'inactiveDate') return false;
					const index = rows.findIndex((r) => r.id === params.id);
					if (index === -1) return false;

					const current = rows[index];
					const isSaved = typeof current.id === 'number';
					const inactiveInPast =
						current.inactiveDate && dayjs.utc(current.inactiveDate).isBefore(dayjs.utc());

					const isLastRow = index === rows.length - 1;

					if (isSaved && inactiveInPast) return false;

					if (params.field === 'inactiveDate') return isLastRow;

					return !!params.colDef.editable;
				}}
			/>
		</Box>
	);
};
